import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  filterHistoryDetailsForDisplay,
  filterHistoryEntriesForDisplay,
  formatHistoryActorLabel,
} from "@aera/data-table";
import { getSigninAuthTokenForApi } from "@/lib/signinPendingAuth";

export type ReceivableHistoryEntryType =
  | "recorded"
  | "opened"
  | "transfer"
  | "matched"
  | "edited"
  | "unmatched"
  | "moved"
  | "split"
  | "undone";

export type ReceivableHistorySummary = {
  headline: string;
  rows: Array<{ label: string; value: string; emphasize?: boolean }>;
  explanation: string;
};

export type ReceivableHistoryEntry = {
  id: string;
  type: ReceivableHistoryEntryType;
  title: string;
  description: string;
  actorId: string;
  actorName: string;
  channelLabel?: string;
  occurredAt: string;
  details?: string[];
  isLatest?: boolean;
  reversible?: boolean;
  availableActions?: string[];
};

export class HistoryLoadError extends Error {
  constructor(message = "โหลดประวัติไม่สำเร็จ") {
    super(message);
    this.name = "HistoryLoadError";
  }
}

type ApiHistoryEntry = {
  id: string;
  type: string;
  title: string;
  description: string;
  actor_name: string;
  channel_label?: string;
  occurred_at: string;
  details?: string[];
  is_latest?: boolean;
  reversible?: boolean;
  available_actions?: string[];
};

function pickField(item: ReceivableListItem, keys: string[]): string | null {
  for (const key of keys) {
    const value = item.fields.find((field) => field.key === key)?.value?.trim();
    if (value) return value;
  }
  return null;
}

export function pickReceivableId(item: ReceivableListItem): string | null {
  return (
    item.receivableId?.trim() ||
    pickField(item, [
      "receivable_id",
      "receivableId",
      "receivable_order_id",
      "receivableOrderId",
    ])
  );
}

export function pickPaymentId(item: ReceivableListItem): string | null {
  return (
    item.paymentId?.trim() ||
    pickField(item, [
      "payment_id",
      "paymentId",
      "payment_order_id",
      "paymentOrderId",
    ])
  );
}

export function pickAddressId(item: ReceivableListItem): string | null {
  return (
    item.addressId?.trim() ||
    pickField(item, [
      "address_id",
      "addressId",
      "address_order_id",
      "addressOrderId",
    ])
  );
}

export type PendingHistorySubject =
  | { kind: "receivable"; id: string }
  | { kind: "payment"; id: string }
  | { kind: "address"; id: string };

export function pickPendingHistorySubject(
  item: ReceivableListItem,
): PendingHistorySubject | null {
  const receivableId = pickReceivableId(item);
  if (receivableId) return { kind: "receivable", id: receivableId };

  const paymentId = pickPaymentId(item);
  if (paymentId) return { kind: "payment", id: paymentId };

  if (isAddressItem(item)) {
    const addressId = pickAddressId(item);
    if (addressId) return { kind: "address", id: addressId };
  }

  return null;
}

function pickAmountLabel(item: ReceivableListItem): string {
  if (item.amountLabel?.trim()) return item.amountLabel.trim();
  return (
    pickField(item, [
      "slip_amount",
      "amount",
      "transfer_amount",
      "outstanding_amount",
      "total_amount",
    ]) ?? "—"
  );
}

function isSlipItem(item: ReceivableListItem): boolean {
  const kind = item.kindLabel?.trim() ?? "";
  return /สลิป/i.test(kind);
}

function isAddressItem(item: ReceivableListItem): boolean {
  const kind = item.kindLabel?.trim() ?? "";
  return /ที่อยู่/i.test(kind);
}

function formatOccurredAt(iso: string): string {
  if (!iso?.trim()) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function mapApiEntry(row: ApiHistoryEntry): ReceivableHistoryEntry {
  const rawActor = (row.actor_name || "").trim();
  return {
    id: row.id,
    type: (row.type as ReceivableHistoryEntryType) || "transfer",
    title: row.title,
    description: row.description,
    actorId: rawActor,
    actorName: formatHistoryActorLabel(rawActor) || "—",
    channelLabel: row.channel_label || undefined,
    occurredAt: formatOccurredAt(row.occurred_at),
    details: filterHistoryDetailsForDisplay(row.details),
    isLatest: row.is_latest,
    reversible: row.reversible === true,
    availableActions: Array.isArray(row.available_actions)
      ? row.available_actions
      : [],
  };
}

export async function fetchReceivableHistory(
  receivableId: string,
  ownerUserId: string,
): Promise<ReceivableHistoryEntry[]> {
  const qs = new URLSearchParams({ owner_user_id: ownerUserId });
  const token = typeof window !== "undefined" ? getSigninAuthTokenForApi() : null;
  const res = await fetch(
    `/api/v1/receivables/${encodeURIComponent(receivableId)}/history?${qs}`,
    {
      headers: {
        Accept: "application/json",
        ...(token
          ? {
              Authorization: `Bearer ${
                token.startsWith("Bearer ") ? token.slice(7) : token
              }`,
            }
          : {}),
      },
    },
  );
  if (!res.ok) {
    throw new HistoryLoadError(`โหลดประวัติไม่สำเร็จ (${res.status})`);
  }
  const data = (await res.json()) as { entries?: ApiHistoryEntry[] };
  return filterHistoryEntriesForDisplay(data.entries ?? []).map(mapApiEntry);
}

export async function fetchPaymentHistory(
  paymentId: string,
  ownerUserId: string,
): Promise<ReceivableHistoryEntry[]> {
  const qs = new URLSearchParams({ owner_user_id: ownerUserId });
  const token = typeof window !== "undefined" ? getSigninAuthTokenForApi() : null;
  const res = await fetch(
    `/api/v1/payments/${encodeURIComponent(paymentId)}/history?${qs}`,
    {
      headers: {
        Accept: "application/json",
        ...(token
          ? {
              Authorization: `Bearer ${
                token.startsWith("Bearer ") ? token.slice(7) : token
              }`,
            }
          : {}),
      },
    },
  );
  if (!res.ok) {
    throw new HistoryLoadError(`โหลดประวัติไม่สำเร็จ (${res.status})`);
  }
  const data = (await res.json()) as { entries?: ApiHistoryEntry[] };
  return filterHistoryEntriesForDisplay(data.entries ?? []).map(mapApiEntry);
}

export async function fetchAddressHistory(
  addressId: string,
  ownerUserId: string,
): Promise<ReceivableHistoryEntry[]> {
  const qs = new URLSearchParams({ owner_user_id: ownerUserId });
  const token = typeof window !== "undefined" ? getSigninAuthTokenForApi() : null;
  const res = await fetch(
    `/api/v1/addresses/${encodeURIComponent(addressId)}/history?${qs}`,
    {
      headers: {
        Accept: "application/json",
        ...(token
          ? {
              Authorization: `Bearer ${
                token.startsWith("Bearer ") ? token.slice(7) : token
              }`,
            }
          : {}),
      },
    },
  );
  if (!res.ok) {
    throw new HistoryLoadError(`โหลดประวัติไม่สำเร็จ (${res.status})`);
  }
  const data = (await res.json()) as { entries?: ApiHistoryEntry[] };
  return (data.entries ?? []).map(mapApiEntry);
}

function buildAddressHistoryFromItem(
  item: ReceivableListItem,
): ReceivableHistoryEntry[] {
  const name =
    pickField(item, ["addr_name", "recipient_name", "customer_ref"]) ??
    item.title?.trim() ??
    "ผู้รับ";
  const phone = item.addrPhone ?? pickField(item, ["addr_phone", "phone"]);
  const addressText = pickField(item, ["address_text", "address", "ที่อยู่"]);
  const createdAt = item.createdAt
    ? formatOccurredAt(item.createdAt)
    : "—";
  const isReview = item.statuses.includes("review");
  const isDismissed = item.statuses.includes("dismissed");
  const isExpired = item.statuses.includes("expired");
  const leftAt =
    pickField(item, ["queue_left_at"]) ||
    (item.createdAt ? formatOccurredAt(item.createdAt) : createdAt);
  const details: string[] = [];
  if (phone) details.push(`เบอร์: ${phone}`);
  if (addressText) details.push(`ที่อยู่: ${addressText}`);

  const entries: ReceivableHistoryEntry[] = [
    {
      id: `address-summary:${item.key}`,
      type: "recorded",
      title: "รับที่อยู่",
      description: `บันทึกที่อยู่ของ ${name}`,
      actorId: "",
      actorName: "—",
      occurredAt: createdAt,
      details,
      isLatest: !isReview && !isDismissed && !isExpired,
    },
  ];

  if (isReview && !isDismissed && !isExpired) {
    entries.push({
      id: `address-summary:${item.key}:review`,
      type: "transfer",
      title: "รอตรวจ",
      description: "ที่อยู่นี้อยู่ในขั้นตรวจก่อนใช้งาน",
      actorId: "",
      actorName: "—",
      occurredAt: createdAt,
      isLatest: false,
    });
  }

  if (isDismissed || isExpired) {
    entries.push({
      id: `address-summary:${item.key}:queue_left`,
      type: "edited",
      title: isDismissed ? "ลบรายการ" : "หมดอายุ",
      description: isDismissed
        ? "ลบออกจากคิวที่อยู่ค้าง"
        : "หมดอายุจากคิวที่อยู่ค้าง",
      actorId: "",
      actorName: "—",
      occurredAt: leftAt,
      isLatest: true,
    });
  }

  return entries;
}

export async function fetchPendingItemHistory(
  item: ReceivableListItem,
  ownerUserId: string,
): Promise<ReceivableHistoryEntry[]> {
  const subject = pickPendingHistorySubject(item);
  if (!ownerUserId.trim()) return [];

  if (!subject) {
    if (isAddressItem(item)) {
      return buildAddressHistoryFromItem(item);
    }
    return [];
  }

  if (subject.kind === "receivable") {
    return fetchReceivableHistory(subject.id, ownerUserId);
  }
  if (subject.kind === "payment") {
    return fetchPaymentHistory(subject.id, ownerUserId);
  }

  try {
    const apiRows = await fetchAddressHistory(subject.id, ownerUserId);
    if (apiRows.length > 0) return apiRows;
  } catch {
    // Dismissed / expired addresses may 404 on older backends — fall back to local summary.
  }
  return buildAddressHistoryFromItem(item);
}

export function buildReceivableHistorySummary(
  item: ReceivableListItem,
): ReceivableHistorySummary {
  const amount = pickAmountLabel(item);
  const sender =
    pickField(item, [
      "slip_sender",
      "slip_sender_name",
      "sender_name",
      "addr_name",
      "customer_ref",
    ]) ?? item.title?.trim() ?? "—";
  const isPending = item.statuses.includes("pending");
  const isReview = item.statuses.includes("review");

  if (isSlipItem(item)) {
    const rows: ReceivableHistorySummary["rows"] = [
      { label: "ยอดสลิป", value: amount, emphasize: true },
      { label: "ผู้โอน", value: sender },
    ];

    return {
      headline: "สรุปสลิปตอนนี้",
      rows,
      explanation: "",
    };
  }

  if (isAddressItem(item)) {
    const phone =
      item.addrPhone ?? pickField(item, ["addr_phone", "phone"]) ?? "—";
    const addressText =
      pickField(item, ["address_text", "address", "ที่อยู่"]) ?? "—";
    return {
      headline: "สรุปที่อยู่ตอนนี้",
      rows: [
        { label: "ชื่อ", value: sender, emphasize: true },
        { label: "เบอร์", value: phone },
        { label: "ที่อยู่", value: addressText },
        {
          label: "สถานะ",
          value: isReview ? "รอตรวจ" : "ค้าง",
        },
      ],
      explanation: isReview
        ? "ที่อยู่นี้รอตรวจก่อนใช้งาน"
        : "ที่อยู่รอดำเนินการ",
    };
  }

  const rows: ReceivableHistorySummary["rows"] = [
    { label: "ยอดค้าง", value: amount, emphasize: true },
    { label: "ลูกค้า", value: sender },
    {
      label: "สถานะ",
      value: [isPending ? "ค้าง" : null, isReview ? "รอตรวจ" : null]
        .filter(Boolean)
        .join(" · ") || "—",
    },
  ];

  return {
    headline: "สรุปยอดค้างตอนนี้",
    rows,
    explanation: "",
  };
}

/** @deprecated use fetchReceivableHistory — kept for tests */
export function buildReceivableHistoryEntries(
  _item: ReceivableListItem,
): ReceivableHistoryEntry[] {
  return [];
}

export function pickLatestHistoryMeta(
  entries: ReceivableHistoryEntry[],
): { label: string; actorName: string; occurredAt: string } | null {
  const latest = entries.find((entry) => entry.isLatest) ?? entries[0];
  if (!latest || latest.occurredAt === "—") return null;
  const actorName = formatHistoryActorLabel(latest.actorName);
  return {
    label: latest.title,
    actorName: actorName || "",
    occurredAt: latest.occurredAt,
  };
}
